import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as AiIcon,
} from '@mui/icons-material'
import {
  AiGenerationRequest,
  AiGenerationUsageInfo,
} from '@/features/internal/drugs/types/soapTemplate'
import AiSoapTemplateGenerationUsageDisplay from '../AiSoapTemplateGenerationUsageDisplay'
import { SECONDARY_ICON_BUTTON_STYLE } from '@/shared/constants/buttonStyles'

interface AiSoapTemplateGenerationModalProps {
  open: boolean
  onClose: () => void
  onGenerate: (requests: AiGenerationRequest[]) => Promise<void>
  usageInfo?: AiGenerationUsageInfo
  loading?: boolean
  error?: string | null
}

/**
 * AI SOAP テンプレート生成モーダル
 */
const AiSoapTemplateGenerationModal: React.FC<
  AiSoapTemplateGenerationModalProps
> = ({ open, onClose, onGenerate, usageInfo, loading = false, error }) => {
  // 生成リクエストの状態管理
  // モーダルが閉じたときに状態をリセットするため、open が false から true に変わったことを検出
  const getInitialRequests = (): AiGenerationRequest[] => [
    { drug_group_name: '', purpose_description: '' },
  ]

  const [requests, setRequests] =
    useState<AiGenerationRequest[]>(getInitialRequests)
  const [lastOpenState, setLastOpenState] = useState(open)

  // モーダルの開閉状態の変化を追跡し、開いたときにリクエストをリセット
  // useEffect を使わず、レンダリング中に状態を派生させる
  if (open !== lastOpenState) {
    setLastOpenState(open)
    if (open) {
      // モーダルが開かれた時のみリクエストをリセット
      setRequests(getInitialRequests())
    }
  }

  // リクエスト項目を追加
  const addRequest = () => {
    setRequests([...requests, { drug_group_name: '', purpose_description: '' }])
  }

  // リクエスト項目を削除
  const removeRequest = (index: number) => {
    if (requests.length > 1) {
      setRequests(requests.filter((_, i) => i !== index))
    }
  }

  // リクエスト項目を更新
  const updateRequest = (
    index: number,
    field: keyof AiGenerationRequest,
    value: string
  ) => {
    const updatedRequests = [...requests]
    updatedRequests[index][field] = value
    setRequests(updatedRequests)
  }

  // フォームの妥当性チェック
  const isValid = requests.every(
    (req) => req.drug_group_name.trim() && req.purpose_description.trim()
  )

  // 生成実行
  const handleGenerate = async () => {
    if (!isValid) return

    try {
      await onGenerate(requests)
      onClose()
    } catch (_err) {
      // エラーが発生した場合はモーダルを閉じない（親でエラー表示される）
    }
  }

  // 利用制限チェック
  const isOverLimit = usageInfo ? usageInfo.remaining <= 0 : true // usageInfoがない場合は制限ありとする
  const canGenerate = !loading && !isOverLimit && isValid

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AiIcon color="action" />
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ opacity: 0.8 }}
          >
            AI SOAPテンプレート生成
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* 利用状況表示 */}
        {usageInfo && (
          <AiSoapTemplateGenerationUsageDisplay usageInfo={usageInfo} />
        )}

        {/* エラー表示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* 生成リクエストフォーム */}
        <Stack spacing={3}>
          {requests.map((request, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Chip
                  label={`テンプレート ${index + 1}`}
                  size="small"
                  sx={{
                    color: 'text.primary',
                    borderColor: 'text.secondary',
                    borderWidth: '1px',
                    backgroundColor: 'transparent',
                  }}
                  variant="outlined"
                />
                {requests.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeRequest(index)}
                    sx={SECONDARY_ICON_BUTTON_STYLE}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <TextField
                fullWidth
                label="医薬品グループ名"
                placeholder="例: 糖尿病用剤、降圧剤、抗菌剤"
                value={request.drug_group_name}
                onChange={(e) =>
                  updateRequest(index, 'drug_group_name', e.target.value)
                }
                margin="normal"
                required
                disabled={loading}
              />
              <TextField
                fullWidth
                label="執筆したいSOAPテンプレートの概要"
                placeholder="例: Sick Day時の対処法についての指導、低血糖症状の対処法指導"
                value={request.purpose_description}
                onChange={(e) =>
                  updateRequest(index, 'purpose_description', e.target.value)
                }
                margin="normal"
                multiline
                rows={3}
                required
                disabled={loading}
              />
            </Box>
          ))}
          {/* 追加ボタン */}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addRequest}
            disabled={loading || requests.length >= 5}
            size="small"
            sx={{
              alignSelf: 'flex-start',
              py: 0.5,
              px: 1.5,
              color: 'text.primary',
              borderColor: 'text.secondary',
              '&:hover': {
                borderColor: 'text.primary',
                backgroundColor: 'action.hover',
              },
            }}
          >
            テンプレートを追加 ({requests.length}/5)
          </Button>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!canGenerate}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AiIcon />
            )
          }
        >
          {loading ? '生成中...' : 'AI生成実行'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AiSoapTemplateGenerationModal
